import { useCallback, useMemo, useRef, useState } from 'react';
import { MediaCapture } from '../../../utils/mediaCapture';
import { buildCompanionSendMessage } from '../constants/musicGenerationAvatarMessages';
import { SCREEN_SHARE_COMPANION_MESSAGES } from '../constants/screenShareCompanionMessages';
import { useDataChannel } from './useDataChannel';

interface UseScreenShareReturn {
  isCapturing: boolean;
  isSupported: boolean;
  startScreenShare: () => Promise<void>;
  stopScreenShare: () => void;
  toggleScreenShare: () => Promise<void>;
}

const checkScreenShareSupport = (): boolean => {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getDisplayMedia === 'function'
  );
};

export const useScreenShare = (
  screenShareCapture: MediaCapture | null,
): UseScreenShareReturn => {
  const [isCapturing, setIsCapturing] = useState(false);
  const isSupported = useMemo(() => checkScreenShareSupport(), []);
  // Use ref instead of state to prevent race conditions and stale closures in PiP window
  const isCapturingRef = useRef(false);
  const currentStream = useRef<MediaStream | null>(null);
  const { sendMessage, isOpen } = useDataChannel();

  const stopScreenShare = useCallback(() => {
    // Stop all tracks in the current stream
    if (currentStream) {
      currentStream.current?.getTracks().forEach((track) => {
        track.stop();
        console.log('Stopped track:', track.kind);
      });
      currentStream.current = null;
      screenShareCapture?.setStream(null);
      setIsCapturing(false);
      isCapturingRef.current = false;

      if (isOpen) {
        const command = {
          type: 'stop_video',
          data: null,
        };
        console.log('Sending stop_video command', command);
        sendMessage(command);
        sendMessage(
          buildCompanionSendMessage(
            SCREEN_SHARE_COMPANION_MESSAGES.stopped,
            true,
            false,
          ),
        );
      }
    }
  }, [currentStream, isOpen, sendMessage, screenShareCapture]);

  const startScreenShare = useCallback(async () => {
    if (isCapturingRef.current) return;

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          frameRate: {
            max: 1,
          },
          width: {
            max: 1280,
          },
          height: {
            max: 720,
          },
        },
        audio: false,
      });

      screenShareCapture?.setStream(stream);
      currentStream.current = stream;
      setIsCapturing(true);
      isCapturingRef.current = true;

      // Add ended handler for when user stops sharing via browser popup
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.addEventListener('ended', () => {
          stopScreenShare();
        });
      }

      if (isOpen) {
        const command = {
          type: 'start_video',
          data: null,
        };
        console.log('Sending start_video command', command);
        sendMessage(command);
        sendMessage(
          buildCompanionSendMessage(
            SCREEN_SHARE_COMPANION_MESSAGES.started,
            true,
            false,
          ),
        );
      }

      // Initialize stream with ended handler (like client.js)
    } catch (error) {
      console.error('Screen capture failed:', error);
      throw error;
    }
  }, [isOpen, sendMessage, screenShareCapture, stopScreenShare]);

  const toggleScreenShare = useCallback(async () => {
    if (isOpen) {
      if (isCapturing) {
        stopScreenShare();
      } else {
        await startScreenShare();
      }
    }
  }, [isCapturing, startScreenShare, stopScreenShare, isOpen]);

  return {
    isCapturing,
    isSupported,
    startScreenShare,
    stopScreenShare,
    toggleScreenShare,
  };
};
